import type { CrossChainStep } from '@/lib/api/0x-cross-chain/types';

import { TextSkeleton } from '@/components/Skeleton';
import { getBridgeDisplayName } from '@/components/TransactionModule/CrossChainMode/bridgeProviders';

import { NATIVE_TOKEN_ADDRESS_PER_CHAIN_ID } from '@/constants/addresses';

import { fontStyles } from '@/styles/typography';

import { BridgeAvatar } from '@/ui/Avatars/BridgeAvatar';
import { ArrowDownIcon } from '@/ui/Icons/ArrowDownIcon';
import { ChevronDownIcon } from '@/ui/Icons/ChevronDownIcon';

import { hasInTokenGasFee, Price, Quote } from '@/utils/0x.types';
import { TokenResult } from '@/utils/0x/token-registry.types';
import { classNames } from '@/utils/classnames';

import { PriceRecapMarketMode, PriceRecapCrossChainMode } from './PriceRecap';
import styles from './styles.module.css';

type CollapsedContentMarketModeProps = {
  mode: 'showMore' | 'showLess';
  isLoading: boolean;
  isWrap: boolean;
  isCurrentSwapGasless: boolean;
  sellToken: TokenResult;
  buyToken: TokenResult;
  chainId: number;
  currentPrice: Price | Quote | undefined;
  nativeTokenUsd: number;
  tokenUsdPrices: { buyTokenUsd: number; sellTokenUsd: number };
};

export const CollapsedContentMarketMode = ({
  mode,
  isLoading,
  isWrap,
  isCurrentSwapGasless,
  sellToken,
  buyToken,
  chainId,
  currentPrice,
  nativeTokenUsd,
  tokenUsdPrices,
}: CollapsedContentMarketModeProps) => {
  return (
    <>
      <span
        className={fontStyles({
          variant: 'BODY_MD',
          class: 'flex items-center text-token-gray-800',
        })}
      >
        {mode === 'showLess' ? (
          <span className={fontStyles({ variant: 'LABEL_MD', class: 'text-token-gray-800' })}>
            Show less
          </span>
        ) : isWrap ? (
          <>
            <span className="flex items-center justify-center bg-primitive-basic-white rounded-xs border border-solid h-5 mr-1.5 w-5 text-token-gray-300">
              <ArrowDownIcon className="size-2.5 -rotate-90" />
            </span>
            Direct{' '}
            {sellToken.address === NATIVE_TOKEN_ADDRESS_PER_CHAIN_ID[chainId] ? 'Wrap' : 'Unwrap'}
          </>
        ) : null}
      </span>

      <span className={classNames('flex', 'items-center', styles.animatedFadeIn)}>
        {mode === 'showMore' &&
          (isLoading || !currentPrice ? (
            <TextSkeleton width={120} height={14} />
          ) : (
            <span
              className={fontStyles({
                variant: 'BODY_MD',
                class: 'text-token-gray-800 flex items-center',
              })}
            >
              <PriceRecapMarketMode
                isCurrentSwapGasless={isCurrentSwapGasless}
                sellToken={sellToken}
                buyToken={buyToken}
                fees={hasInTokenGasFee(currentPrice) ? currentPrice.fees : undefined}
                totalNetworkFee={
                  'totalNetworkFee' in currentPrice ? currentPrice.totalNetworkFee : undefined
                }
                nativeTokenUsd={nativeTokenUsd}
                tokenUsdPrices={tokenUsdPrices}
              />
            </span>
          ))}
        <ChevronDownIcon
          className={classNames(
            'text-token-gray-800',
            'ml-1',
            'transition-transform',
            'duration-300',
            'origin-[center_center]',
            mode === 'showMore' ? 'rotate-0' : 'rotate-180',
          )}
        />
      </span>
    </>
  );
};

type CollapsedContentCrossChainModeProps = {
  mode: 'showMore' | 'showLess';
  isLoading: boolean;
  steps: CrossChainStep[];
  totalNetworkFee: string;
  nativeTokenUsd: number;
  chainType: 'evm' | 'svm';
};

export const CollapsedContentCrossChainMode = ({
  mode,
  isLoading,
  steps,
  totalNetworkFee,
  nativeTokenUsd,
  chainType,
}: CollapsedContentCrossChainModeProps) => {
  const bridgeSteps = steps.filter((step) => step.type === 'bridge' && step.provider);

  return (
    <>
      <span
        className={fontStyles({
          variant: 'BODY_MD',
          class: 'flex items-center text-token-gray-800',
        })}
      >
        {mode === 'showLess' ? (
          <span className={fontStyles({ variant: 'LABEL_MD', class: 'text-token-gray-800' })}>
            Show less
          </span>
        ) : isLoading ? (
          <TextSkeleton width={120} height={14} />
        ) : bridgeSteps.length ? (
          <span className="flex items-center gap-1">
            {bridgeSteps.map((step, index) => (
              <span key={`${step.provider}-${index}`} className="flex items-center gap-1">
                {index > 0 && <span className="text-token-gray-700">{'\u2192'}</span>}
                <BridgeAvatar name={step.provider ?? ''} size={16} />
                <span className="text-token-gray-800">{getBridgeDisplayName(step.provider)}</span>
              </span>
            ))}
          </span>
        ) : null}
      </span>

      <span className={classNames('flex', 'items-center', styles.animatedFadeIn)}>
        {mode === 'showMore' &&
          (isLoading ? (
            <TextSkeleton width={120} height={14} />
          ) : (
            <span
              className={fontStyles({
                variant: 'BODY_MD',
                class: 'text-token-gray-800 flex items-center',
              })}
            >
              <PriceRecapCrossChainMode
                totalNetworkFee={totalNetworkFee}
                nativeTokenUsd={nativeTokenUsd}
                chainType={chainType}
              />
            </span>
          ))}
        <ChevronDownIcon
          className={classNames(
            'text-token-gray-800',
            'ml-1',
            'transition-transform',
            'duration-300',
            'origin-[center_center]',
            mode === 'showMore' ? 'rotate-0' : 'rotate-180',
          )}
        />
      </span>
    </>
  );
};
